import React, { useContext } from 'react';
import {
  Badge,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Typography
} from '@one-thd/sui-atomic-components';
import { PurchaseHistory } from '@one-thd/sui-icons';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { FulfillmentAddressDetails } from './FulfillmentAddressDetails';
import { FulfillmentLineItems } from './FulfillmentLineItems';
import { FulfillmentDetailsActions } from './FulfillmentDetailsActions';
import { FulfillmentDeliveryTracker } from './FulfillmentDeliveryTracker';
import { CurbsidePickup } from './CurbsidePickup';
import {
  FulfillmentActionsSkeleton,
  FulfillmentAddressSkeleton,
  FulfillmentLineItemsSkeleton,
  FulfillmentStatusSkeleton,
  FulfillmentTitleSkeleton
} from './FulfillmentDrawerSkeleton';
import { getOrderStatus, isCurbsidePickupEligible } from './utils';

/**
 * Ported one-to-one (OOP-4059) from `@thd-olt-component-react/active-orders`'s own
 * FulfillmentDetailsDrawer (active-orders-carousel effort) — same component tree, conditional
 * logic, and data contract (`fulfillmentGroup: any`, no exported type upstream either).
 *
 * Local port instead of the package import this repo used before: the real package pulls in
 * `@delivery/tracking` (via `FulfillmentDeliveryTracker`), which has a build defect — its
 * compiled output uses extensionless relative ESM imports, invalid under Node's strict ESM
 * resolver. A `pnpm patch` fixed the first symptom (missing `"type": "module"` causing a hard
 * SyntaxError) but exposed a deeper one in the production prerender build (`ERR_MODULE_NOT_FOUND`
 * on every relative import) that would require patching import specifiers across its whole
 * compiled `dist` tree — too fragile to carry long-term, and not owned by the carousel team
 * either (delivery-tracking is a third team's package, consumed by both repos). See
 * FulfillmentDeliveryTracker.tsx's doc comment for the substitution made there.
 *
 * KNOWN GAP (unchanged from the import-based version, tracked in ActiveOrdersTable.tsx): the real
 * activeOrders GraphQL query doesn't yet request the fields this tree expects (`fulfillmentType`,
 * `storePickup`, `deliveryTrackerData`, `bodfsDeliveryAndHandling`, `shippingAndHandling`,
 * `lineItems[].statusCode/statusDescription`, etc.) — `RawActiveDistribution` doesn't carry them,
 * so most sections below render their defensive empty/null state until `activeOrdersDataModel.ts`
 * is extended and the field names are confirmed against the live schema. This port doesn't change
 * that; it only removes the broken third-party dependency.
 */
export interface FulfillmentDetailsDrawerProps {
  open?: boolean;
  onClose?: () => void;
  fulfillmentGroup?: any;
  isMobile?: boolean;
  /**
   * True while the drawer's orderDetails query is in flight. Everything derived from orderDetails
   * (status title/text, tracker, addresses, line items, actions) renders as skeletons instead of the
   * empty/default state it would show from the raw distribution alone; the raw distribution's own
   * header (project/PO-Job/order number) stays visible.
   */
  isLoading?: boolean;
}

export const FulfillmentDetailsDrawer: React.FC<FulfillmentDetailsDrawerProps> = ({
  open,
  onClose,
  fulfillmentGroup,
  isMobile,
  isLoading = false
}) => {
  const { isConsumerApp } = useContext(ExperienceContext);

  if (!fulfillmentGroup) {
    return null;
  }

  const { statusText, date, timeWindow, deliveryStatusText } = getOrderStatus(fulfillmentGroup);
  const statusDate = timeWindow?.short ? date?.short : date?.long;
  const title = isLoading ? (
    <FulfillmentTitleSkeleton />
  ) : (
    <Typography variant="h4" weight="bold">
      {statusText} {statusDate}
      {timeWindow?.short && `, ${timeWindow.short}`}
    </Typography>
  );

  const curbsidePickupEligible = isCurbsidePickupEligible(fulfillmentGroup) && isConsumerApp;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      position="right"
      role="dialog"
      aria-modal="true"
      aria-label="Fulfillment details"
      aria-busy={isLoading}
      data-testid="fulfillment-details-drawer"
    >
      <DrawerHeader title={title} onClose={onClose} />
      <DrawerBody overflow="scroll">
        <div className="sui-bg-primary">
          <div className="sui-border-solid sui-border-b-1 sui-border-b-primary sui-pb-4">
            {(fulfillmentGroup?.projectName || fulfillmentGroup?.poJobName) && (
              <div className="sui-flex sui-flex-row sui-gap-2 sui-mb-1">
                {fulfillmentGroup?.projectName && (
                  <Badge color="info" size="small" variant="strong">
                    {fulfillmentGroup.projectName}
                  </Badge>
                )}
                {fulfillmentGroup?.poJobName && (
                  <Badge color="info" size="small" variant="strong">
                    {fulfillmentGroup.poJobName}
                  </Badge>
                )}
              </div>
            )}
            {fulfillmentGroup?.orderNumber && (
              <Typography variant="body-sm" color="subtle">
                Part of Order #{fulfillmentGroup.orderNumber}
              </Typography>
            )}
            {isLoading ? (
              <FulfillmentStatusSkeleton />
            ) : (
              <>
                <div className="sui-flex sui-flex-row sui-items-center sui-gap-2 sui-mt-4">
                  <span className="sui-text-brand">
                    <PurchaseHistory />
                  </span>
                  <Typography variant="h6">
                    <span className="sui-text-brand">Delivery Status</span>
                  </Typography>
                </div>
                <Typography variant="h4" weight="display" data-testid="delivery-status" uppercase>
                  {deliveryStatusText}
                </Typography>
                <div className="sui-mt-3">
                  <FulfillmentDeliveryTracker fulfillmentGroup={fulfillmentGroup} isMobile={isMobile} />
                </div>
              </>
            )}
          </div>

          {isLoading ? (
            <FulfillmentAddressSkeleton />
          ) : (
            <FulfillmentAddressDetails fulfillmentGroup={fulfillmentGroup} />
          )}
        </div>
        <div className="sui-bg-primary sui-mt-4 sui-mb-4 sui-p-4">
          {isLoading ? (
            <FulfillmentLineItemsSkeleton />
          ) : (
            <FulfillmentLineItems fulfillmentGroup={fulfillmentGroup} />
          )}
        </div>
        {!isLoading && curbsidePickupEligible && (
          <div className="sui-bg-primary sui-mt-4 sui-mb-4 sui-p-4">
            <CurbsidePickup storeId={fulfillmentGroup?.fulfillmentStore} orderId={fulfillmentGroup?.orderNumber} />
          </div>
        )}
      </DrawerBody>
      <DrawerFooter className="sui-bg-primary sui-p-4 sui-bottom-0 sui-sticky sui-shadow-sm">
        {isLoading ? <FulfillmentActionsSkeleton /> : <FulfillmentDetailsActions fulfillmentGroup={fulfillmentGroup} />}
      </DrawerFooter>
    </Drawer>
  );
};
